<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { match } from "@/helpers/string";
import { useConfigurationStore } from "@/store/configurationStore";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { IJobStored } from '@/types/job';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownInput from "@/dsComponents/Dropdown/DropdownInput.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import DropdownSeparator from "@/dsComponents/Dropdown/DropdownSeparator.vue";

interface JobsDropdownMultipleProps {
    selectedJobs: IJobStored[];
    placeholder?: string;
    icon?: string;
    error?: boolean;
    width?: string;
    height?: string;
    readonly?: boolean;
    fitContent?: boolean;
    fullWidth?: boolean;
}

const props = withDefaults(defineProps<JobsDropdownMultipleProps>(), {
    icon: "work",
    width: "320px",
    height: "220px",
    fullWidth: false,
});

const emit = defineEmits<{
    "update:selectedJobs": [value: IJobStored[]];
}>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const open = ref(false);
const inputFocused = ref(false);
const search = ref("");

const jobs = ref<IJobStored[]>(configStore.jobOptions.sort((a: IJobStored, b: IJobStored) => (a.label > b.label ? 1 : -1)));

const dropdownJobs = computed(() => {
    return jobs.value.filter((job) => {
        const isNotAlreadySelected = !props.selectedJobs.some((selectedJob) => selectedJob.value === job.value);
        if (search.value && isNotAlreadySelected) {
            return match(job.label, search.value);
        }

        return isNotAlreadySelected;
    });
});

const triggerLabel = computed(() => {
    if (props.selectedJobs.length === 0) return props.placeholder ?? t("page.order.jobPlaceholder");
    // biome-ignore lint/style/useTemplate: Init Biome
    return `${props.selectedJobs[0].label} ${props.selectedJobs.length < 2 ? "" : " +" + props.selectedJobs.length}`;
});

const handleDropdownChange = (eventName: supporttedEventNames) => {
    if (eventName === "click") {
        open.value = !open.value;
        inputFocused.value = open.value;
    } else if (eventName === "clickaway") {
        open.value = false;
        inputFocused.value = false;
    }
};

const handleSelectJob = (job: IJobStored) => {
    if (props.selectedJobs.some((selectedJob) => selectedJob.value === job.value)) {
        emit(
            "update:selectedJobs",
            props.selectedJobs.filter((selectedJob) => selectedJob.value !== job.value),
        );
        return;
    }

    emit("update:selectedJobs", [...props.selectedJobs, job]);
};
</script>

<template>
    <div class="jobs-dropdown-multiple">
        <ControlledDropdown
            :open="open"
            :width="width"
            :max-height="height"
            :fit-content="fitContent"
            :full-width="fullWidth"
            @change="handleDropdownChange"
        >
            <template #trigger-zone>
                <Dropdown :width="width" :max-height="height" mode="hover" :no-show="selectedJobs.length <= 1 || open">
                    <template #trigger-zone>
                        <ComboButton
                            :icon-left="icon"
                            :placeholder="!selectedJobs.length"
                            :error="error"
                            :readonly="readonly"
                            :full-width="fullWidth"
                        >
                            {{ triggerLabel }}
                        </ComboButton>
                    </template>
                    <template #dropdown-content>
                        <div v-for="job in selectedJobs" :key="job.value">
                            <DropdownItem :label="job.label" no-hover />
                        </div>
                    </template>
                </Dropdown>
            </template>
            <template #dropdown-content>
                <div v-if="jobs.length" class="jobs-dropdown-multiple__dropdown-header">
                    <DropdownInput v-model="search" :focused="inputFocused" />
                </div>
                <template v-if="selectedJobs.length">
                    <DropdownItem
                        v-for="job in selectedJobs"
                        :key="job.value"
                        :label="job.label"
                        checked
                        checkbox
                        stop
                        @select="handleSelectJob(job)"
                    />
                </template>
                <template v-if="dropdownJobs.length">
                    <DropdownSeparator v-if="selectedJobs.length" />
                    <DropdownItem
                        v-for="job in dropdownJobs"
                        :key="job.value"
                        :label="job.label"
                        :checked="selectedJobs.some((selectedJob) => selectedJob.value === job.value)"
                        checkbox
                        stop
                        @select="handleSelectJob(job)"
                    />
                </template>
            </template>
        </ControlledDropdown>
    </div>
</template>

<style lang="scss">
.jobs-dropdown-multiple__dropdown-header {
    padding-bottom: var(--spacing-sm, 6px);
}
</style>
